import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const fmt  = (n, d=2) => n != null && n !== '' ? Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d}) : '—'
const fmtP = (n) => n != null ? (n>=0?'+':'')+Number(n).toFixed(1)+'%' : '—'

const SearchIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium
      ${type==='success' ? 'bg-[#1A7A4A] text-white' : 'bg-red-600 text-white'}`}>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">✕</button>
    </div>
  )
}

export default function RateAnalysis({ initProject }) {
  const [projects, setProjects]           = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [rows, setRows]                   = useState([])  // analysis rows per sub_code
  const [loading, setLoading]             = useState(false)
  const [toast, setToast]                 = useState(null)
  const [search, setSearch]               = useState('')
  const [costCodeFilter, setCostCodeFilter] = useState('all')
  const [costCodes, setCostCodes]         = useState([])

  const showToast = (msg, type='success') => setToast({ message: msg, type })

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadData(selectedProject.id)
      supabase.from('cost_codes').select('id,code,description').eq('project_id', selectedProject.id).order('code')
        .then(({ data }) => setCostCodes(data || []))
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadData = useCallback(async (projectId) => {
    setLoading(true)

    // 1. Budget from tender_direct_items — resource rows only
    const { data: tenderRows } = await supabase
      .from('tender_direct_items')
      .select('id, budget_material_code, labour_code, budget_sc_code, budget_equipment_code, description, resource_description, cost_code, row_type, qty, unit, unit_rate, total_budget, material_total, labour_total, subcontractor_total, equipment_total, package_name')
      .eq('project_id', projectId)
      .in('row_type', ['material', 'labour', 'subcontract', 'equipment'])

    // 2. Commitment from po_line_items
    const { data: poLines } = await supabase
      .from('po_line_items')
      .select('sub_code, qty, unit_rate, amount_exc_vat, purchase_orders!inner(project_id, status)')
      .eq('purchase_orders.project_id', projectId)

    const approvedLines = (poLines || []).filter(l =>
      ['approved', 'closed'].includes(l.purchase_orders?.status)
    )

    // 3. Actual from store_issues
    const { data: issues } = await supabase
      .from('store_issues')
      .select('sub_code, qty_issued, amount')
      .eq('project_id', projectId)

    // 4. Build analysis rows — sub_code per resource type
    const subCodeMap = {}

    ;(tenderRows || []).forEach(b => {
      let sc = null
      if (b.row_type === 'material')    sc = b.budget_material_code
      else if (b.row_type === 'labour') sc = b.labour_code
      else if (b.row_type === 'subcontract') sc = b.budget_sc_code
      else if (b.row_type === 'equipment')   sc = b.budget_equipment_code
      if (!sc) return

      if (!subCodeMap[sc]) {
        subCodeMap[sc] = {
          sub_code:     sc,
          description:  b.resource_description || b.description || '',
          cost_code:    b.cost_code || '',
          row_type:     b.row_type,
          budget_total: 0,
          budget_qty:   0,
          budget_rate:  0,
          comm_qty:     0,
          comm_total:   0,
          actual_qty:   0,
          actual_total: 0,
        }
      }
      subCodeMap[sc].budget_qty   += parseFloat(b.qty) || 0
      if (parseFloat(b.unit_rate)) subCodeMap[sc].budget_rate = parseFloat(b.unit_rate)
      // Use the correct total column per resource type
      let rowTotal = 0
      if (b.row_type === 'material')    rowTotal = parseFloat(b.material_total)    || parseFloat(b.total_budget) || 0
      else if (b.row_type === 'labour') rowTotal = parseFloat(b.labour_total)      || parseFloat(b.total_budget) || 0
      else if (b.row_type === 'subcontract') rowTotal = parseFloat(b.subcontractor_total) || parseFloat(b.total_budget) || 0
      else if (b.row_type === 'equipment')   rowTotal = parseFloat(b.equipment_total)     || parseFloat(b.total_budget) || 0
      subCodeMap[sc].budget_total += rowTotal
    })

    approvedLines.forEach(l => {
      const sc = l.sub_code
      if (!sc || !subCodeMap[sc]) return
      subCodeMap[sc].comm_qty   += parseFloat(l.qty)            || 0
      subCodeMap[sc].comm_total += parseFloat(l.amount_exc_vat) || 0
    })

    ;(issues || []).forEach(l => {
      const sc = l.sub_code
      if (!sc || !subCodeMap[sc]) return
      subCodeMap[sc].actual_qty   += parseFloat(l.qty_issued) || 0
      subCodeMap[sc].actual_total += parseFloat(l.amount)     || 0
    })

    const result = Object.values(subCodeMap).map(r => {
      const comm_rate   = r.comm_qty   > 0 ? r.comm_total   / r.comm_qty   : 0
      const actual_rate = r.actual_qty > 0 ? r.actual_total / r.actual_qty : 0
      const rate_var_comm       = r.budget_rate > 0 ? comm_rate   - r.budget_rate : null
      const rate_var_actual     = r.budget_rate > 0 ? actual_rate - r.budget_rate : null
      const rate_var_comm_pct   = r.budget_rate > 0 && rate_var_comm   != null ? (rate_var_comm   / r.budget_rate) * 100 : null
      const rate_var_actual_pct = r.budget_rate > 0 && rate_var_actual != null ? (rate_var_actual / r.budget_rate) * 100 : null
      return {
        ...r, comm_rate, actual_rate,
        rate_var_comm, rate_var_actual,
        rate_var_comm_pct, rate_var_actual_pct,
        total_var_comm:   r.comm_total   - r.budget_total,
        total_var_actual: r.actual_total - r.budget_total,
        remaining_budget: r.budget_total - r.comm_total,
      }
    })

    setRows(result)
    setLoading(false)
  }, [])

  // ── Filter ────────────────────────────────────────────────
  const filtered = rows.filter(r => {
    const matchSearch = !search ||
      r.sub_code.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase())
    const matchCC = costCodeFilter === 'all' || r.cost_code === costCodeFilter
    return matchSearch && matchCC
  })

  // ── KPIs ─────────────────────────────────────────────────
  const totalBudget  = filtered.reduce((s,r) => s + r.budget_total, 0)
  const totalComm    = filtered.reduce((s,r) => s + r.comm_total, 0)
  const totalActual  = filtered.reduce((s,r) => s + r.actual_total, 0)
  const overRateCt   = filtered.filter(r => r.rate_var_comm != null && r.rate_var_comm > 0).length

  if (!selectedProject) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-[#1C1A18] mb-6">Rate Analysis</h1>
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center">
          <p className="text-sm text-[#6B7280] mb-4">اختر مشروع</p>
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            {projects.map(p => (
              <button key={p.id} onClick={() => setSelectedProject(p)}
                className="text-left p-4 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#1B3A6B] transition-colors">
                <div className="font-semibold text-[#1C1A18] text-sm">{p.name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1C1A18]">Rate Analysis</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{selectedProject.name} · Sub Code Level</p>
        </div>
        <button onClick={() => loadData(selectedProject.id)}
          className="px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
          ↻ Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Budget Total',      value: fmt(totalBudget, 0),  color: 'text-[#1C1A18]' },
          { label: 'Committed (POs)',   value: fmt(totalComm, 0),    color: 'text-[#1B3A6B]',
            sub: totalBudget > 0 ? `${((totalComm/totalBudget)*100).toFixed(1)}% of budget` : '' },
          { label: 'Actual (Issues)',   value: fmt(totalActual, 0),  color: 'text-[#1A7A4A]',
            sub: totalBudget > 0 ? `${((totalActual/totalBudget)*100).toFixed(1)}% of budget` : '' },
          { label: 'Over-Rate Items',   value: overRateCt,           color: overRateCt > 0 ? 'text-red-600' : 'text-[#1A7A4A]',
            sub: 'PO rate > Budget rate' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <p className="text-xs text-[#9CA3AF] uppercase font-semibold mb-1">{k.label}</p>
            <p className={`text-xl font-bold font-mono ${k.color}`}>{k.value}</p>
            {k.sub && <p className="text-xs text-[#9CA3AF] mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"><SearchIcon/></span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search sub code or description..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
        </div>
        <select value={costCodeFilter} onChange={e => setCostCodeFilter(e.target.value)}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none text-[#6B7280]">
          <option value="all">All Cost Codes</option>
          {[...new Set(rows.map(r => r.cost_code).filter(Boolean))].sort().map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="text-xs text-[#9CA3AF] ml-auto">{filtered.length} sub codes</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#9CA3AF]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#9CA3AF]">
            {rows.length === 0 ? 'مفيش بيانات — ارفع Budget أولاً' : 'مفيش نتائج'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
              <tr>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold whitespace-nowrap">Sub Code</th>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Description</th>
                <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold whitespace-nowrap">Cost Code</th>
                {/* Budget */}
                <th className="px-3 py-3 text-right text-blue-600 uppercase font-semibold whitespace-nowrap border-l border-[#E5E7EB]">Bud. Qty</th>
                <th className="px-3 py-3 text-right text-blue-600 uppercase font-semibold whitespace-nowrap">Bud. Rate</th>
                <th className="px-3 py-3 text-right text-blue-600 uppercase font-semibold whitespace-nowrap">Bud. Total</th>
                {/* Commitment */}
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold whitespace-nowrap border-l border-[#E5E7EB]">Comm. Qty</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold whitespace-nowrap">Comm. Rate</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold whitespace-nowrap">Comm. Total</th>
                <th className="px-3 py-3 text-right text-[#1B3A6B] uppercase font-semibold whitespace-nowrap">Rate Var.</th>
                {/* Actual */}
                <th className="px-3 py-3 text-right text-[#1A7A4A] uppercase font-semibold whitespace-nowrap border-l border-[#E5E7EB]">Act. Qty</th>
                <th className="px-3 py-3 text-right text-[#1A7A4A] uppercase font-semibold whitespace-nowrap">Act. Rate</th>
                <th className="px-3 py-3 text-right text-[#1A7A4A] uppercase font-semibold whitespace-nowrap">Act. Total</th>
                <th className="px-3 py-3 text-right text-[#1A7A4A] uppercase font-semibold whitespace-nowrap">Rate Var.</th>
                {/* Remaining */}
                <th className="px-3 py-3 text-right text-amber-600 uppercase font-semibold whitespace-nowrap border-l border-[#E5E7EB]">Remaining</th>
              </tr>
              {/* Column group labels */}
              <tr className="bg-[#F3F4F6] text-[10px]">
                <td colSpan={3}/>
                <td colSpan={3} className="px-3 py-1 text-center text-blue-500 font-semibold border-l border-[#E5E7EB]">BUDGET</td>
                <td colSpan={4} className="px-3 py-1 text-center text-[#1B3A6B] font-semibold border-l border-[#E5E7EB]">COMMITMENT (Approved POs)</td>
                <td colSpan={4} className="px-3 py-1 text-center text-[#1A7A4A] font-semibold border-l border-[#E5E7EB]">ACTUAL (Store Issues)</td>
                <td className="border-l border-[#E5E7EB]"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {filtered.map(r => {
                const commOver   = r.rate_var_comm   != null && r.rate_var_comm   > 0
                const actualOver = r.rate_var_actual != null && r.rate_var_actual > 0
                return (
                  <tr key={r.sub_code} className={`hover:bg-[#F9FAFB] ${commOver ? 'bg-red-50/30' : ''}`}>
                    <td className="px-3 py-2.5 font-mono font-semibold text-[#1B3A6B]">{r.sub_code}</td>
                    <td className="px-3 py-2.5 text-[#1C1A18] max-w-xs truncate">{r.description}</td>
                    <td className="px-3 py-2.5 text-[#6B7280]">
                      {r.cost_code && <span className="font-mono text-[10px]">{r.cost_code}</span>}
                    </td>
                    {/* Budget */}
                    <td className="px-3 py-2.5 text-right text-[#6B7280] border-l border-[#F3F4F6]">{fmt(r.budget_qty)}</td>
                    <td className="px-3 py-2.5 text-right text-[#6B7280]">{fmt(r.budget_rate)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-blue-600">{fmt(r.budget_total, 0)}</td>
                    {/* Commitment */}
                    <td className="px-3 py-2.5 text-right text-[#6B7280] border-l border-[#F3F4F6]">{r.comm_qty > 0 ? fmt(r.comm_qty) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-[#6B7280]">{r.comm_rate > 0 ? fmt(r.comm_rate) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#1B3A6B]">{r.comm_total > 0 ? fmt(r.comm_total, 0) : '—'}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.rate_var_comm != null && r.comm_rate > 0 ? (
                        <span className={`font-semibold ${commOver ? 'text-red-600' : 'text-[#1A7A4A]'}`}>
                          {commOver ? '▲' : '▼'} {fmtP(r.rate_var_comm_pct)}
                        </span>
                      ) : '—'}
                    </td>
                    {/* Actual */}
                    <td className="px-3 py-2.5 text-right text-[#6B7280] border-l border-[#F3F4F6]">{r.actual_qty > 0 ? fmt(r.actual_qty) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-[#6B7280]">{r.actual_rate > 0 ? fmt(r.actual_rate) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#1A7A4A]">{r.actual_total > 0 ? fmt(r.actual_total, 0) : '—'}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.rate_var_actual != null && r.actual_rate > 0 ? (
                        <span className={`font-semibold ${actualOver ? 'text-red-600' : 'text-[#1A7A4A]'}`}>
                          {actualOver ? '▲' : '▼'} {fmtP(r.rate_var_actual_pct)}
                        </span>
                      ) : '—'}
                    </td>
                    {/* Remaining */}
                    <td className="px-3 py-2.5 text-right border-l border-[#F3F4F6]">
                      <span className={`font-semibold ${r.remaining_budget < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                        {fmt(r.remaining_budget, 0)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {/* Grand Total */}
            <tfoot className="bg-[#F9FAFB] border-t-2 border-[#E5E7EB]">
              <tr className="font-semibold">
                <td colSpan={3} className="px-3 py-3 text-xs font-bold text-[#6B7280] uppercase">Grand Total</td>
                <td colSpan={2} className="border-l border-[#F3F4F6]"/>
                <td className="px-3 py-3 text-right text-sm font-bold text-blue-600">{fmt(totalBudget, 0)}</td>
                <td colSpan={2} className="border-l border-[#F3F4F6]"/>
                <td className="px-3 py-3 text-right text-sm font-bold text-[#1B3A6B]">{fmt(totalComm, 0)}</td>
                <td className="px-3 py-3 text-right text-sm font-bold">
                  {totalBudget > 0 ? (
                    <span className={totalComm > totalBudget ? 'text-red-600' : 'text-[#1A7A4A]'}>
                      {fmtP(((totalComm - totalBudget)/totalBudget)*100)}
                    </span>
                  ) : '—'}
                </td>
                <td colSpan={2} className="border-l border-[#F3F4F6]"/>
                <td className="px-3 py-3 text-right text-sm font-bold text-[#1A7A4A]">{fmt(totalActual, 0)}</td>
                <td className="px-3 py-3 text-right text-sm font-bold">
                  {totalBudget > 0 ? (
                    <span className={totalActual > totalBudget ? 'text-red-600' : 'text-[#1A7A4A]'}>
                      {fmtP(((totalActual - totalBudget)/totalBudget)*100)}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-right text-sm font-bold border-l border-[#F3F4F6]">
                  <span className={totalBudget - totalComm < 0 ? 'text-red-600' : 'text-amber-600'}>
                    {fmt(totalBudget - totalComm, 0)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  )
}
